import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import FAQSection from '../../components/FAQSection'

export default function LionTracking() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  const faqs = [
    { question: 'Are we guaranteed to see lions?', answer: 'Yes, the success rate is over 95% because we use radio telemetry to track their exact location.' },
    { question: 'Is it safe?', answer: 'Completely. You remain in a research vehicle with an armed ranger and experienced researcher at all times.' },
    { question: 'Can children participate?', answer: 'The minimum age is 15 years due to the scientific nature and duration of the activity.' },
    { question: 'Where does the money go?', answer: 'Your permit fee goes directly to the Uganda Carnivore Program to fund vital conservation work, research, and community initiatives.' }
  ];

  return (
    <div>
      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1551033406-611cf9a28f67?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Lion Tracking Research Experience
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Join researchers to track collared lions using telemetry on the Kasenyi Plains
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(320+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-shield-halved" className="w-4 h-4 mr-2" />
                UWA Licensed
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-graduation-cap" className="w-4 h-4 mr-2" />
                Research Program
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-leaf" className="w-4 h-4 mr-2" />
                Conservation Focused
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Research Experience
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                An Exclusive Wildlife Research Adventure
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Lion Tracking Research Experience offers a unique opportunity to join professional wildlife researchers from the Uganda Carnivore Program as they monitor collared lions across Queen Elizabeth National Park's Kasenyi Plains. Participants use radio telemetry equipment to locate specific lions, listening to tracking signals through headphones and following researchers as they collect vital behavioral data. This isn't a typical game drive—this is <strong>lion tracking research</strong>, and you're an active participant in one of Uganda's most important carnivore conservation programs.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  The Lion Tracking Research Experience offers something truly extraordinary: the opportunity to join researchers from the prestigious Uganda Carnivore Program as they monitor and study the park's lion population using cutting-edge GPS and VHF radio telemetry technology. Unlike conventional game drives where sightings are opportunistic, this exclusive program uses science to locate specific collared lions, providing unparalleled insights into their movements, behavior, hunting patterns, and social structures.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  What makes this experience profoundly meaningful is understanding the conservation context behind the research. The program works tirelessly on human-wildlife conflict mitigation, helping communities living adjacent to the park protect their livestock while coexisting peacefully with predators. Your permit fee directly supports these initiatives, making you a vital stakeholder in lion conservation.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Experience Highlights
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-satellite-dish', title: 'Telemetry Tracking', description: 'Use radio tracking equipment to locate collared lions' },
                  { icon: 'fa-leaf', title: 'Conservation Impact', description: 'Directly fund vital predator research and protection' },
                  { icon: 'fa-paw', title: 'Predator Behavior', description: 'Learn about hunting strategies and pride dynamics' },
                  { icon: 'fa-graduation-cap', title: 'Expert Insights', description: 'Guided by professional carnivore researchers' },
                  { icon: 'fa-camera', title: 'Photography', description: 'Get closer access for stunning wildlife photos' },
                  { icon: 'fa-heart', title: 'Pride Dynamics', description: 'Observe social structures and family bonds' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Research Session Timeline
              </h2>
              <div className="space-y-6">
                {[
                  { time: '06:00', title: 'Pickup & Briefing', desc: 'Early morning pickup from your lodge. Meet your researcher and ranger for a briefing on the project and safety protocols.' },
                  { time: '06:30', title: 'Tracking Begins', desc: 'Board the research vehicle. Learn to use the telemetry antenna and receiver to scan for collar frequencies.' },
                  { time: '07:30', title: 'Lion Contact', desc: 'Locate the pride. Approach quietly to observe them resting, grooming, or feeding after a night hunt.' },
                  { time: '08:30', title: 'Data Collection', desc: 'Assist in recording location, behavior, and health data. Take photos for identification records.' },
                  { time: '09:30', title: 'Return Drive', desc: 'Head back through the park, looking for other wildlife like leopards or hyenas on the way.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-camera" className="w-6 h-6" />
                    Gear
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Camera with telephoto lens</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Binoculars</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Notebook & pen</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Personal Items
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Neutral colored clothing</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Sun protection (hat, glasses)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span>Water bottle</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={faqs} title="Common Questions" />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Queen Elizabeth Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/chimp-kyambura-gorge" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1540573133985-87b6da6d54a9?w=800"
                      alt="Kyambura Chimps"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Chimp Trekking - Kyambura</h3>
                    <p className="text-gray-700 mb-4">Trek chimps in the "Valley of Apes".</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>

                <Link to="/activities/crater-lakes-drive" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=800"
                      alt="Crater Lakes"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Crater Lakes Drive</h3>
                    <p className="text-gray-700 mb-4">Scenic drive through explosion craters.</p>
                    <p className="text-safari-gold font-bold">Activity fees apply</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 3-4 hours</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Morning/Evening</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Activity Fee:</strong> Contact for current rates</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-car" className="w-6 h-6 text-safari-green" />
                    <span><strong>Type:</strong> Vehicle Safari</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group:</strong> Limited seats</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-user" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 15 years</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Experience
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Research permit (mandatory)",
                    "Researcher guide",
                    "Telemetry equipment",
                    "Armed ranger escort",
                    "Contribution to conservation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-times-circle" className="w-6 h-6 text-red-600" />
                  What's Not Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Park entrance fees",
                    "Transport to start point",
                    "Meals and drinks",
                    "Tips for team",
                    "Accommodation"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-times" className="w-5 h-5 text-red-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Health & Safety
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Safety:</strong> Stay in vehicle at all times.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> No specific requirements.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Lion Tracking Research"
      />
    </div>
  )
}
